// apps/web/src/components/Marketplace.js
import React, { useEffect, useState } from 'react';
import { useToast } from './Toast';

/**
 * Marketplace - Full-page template browsing and purchasing
 * Shows ALL templates (free + premium)
 * Users can browse, search, filter, and purchase/get templates
 */
export default function Marketplace() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [ownedTemplates, setOwnedTemplates] = useState(new Set());
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priceFilter, setPriceFilter] = useState('all');

  // Load templates and user's library on mount
  useEffect(() => {
    loadTemplates();
    loadUserLibrary();
  }, []);

  // Apply filters whenever they change
  useEffect(() => {
    applyFilters();
  }, [templates, searchQuery, categoryFilter, priceFilter]);

  async function loadTemplates() {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/templates`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('accessToken')
            ? { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            : {}),
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (err) {
      toast.error('Failed to load templates: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function loadUserLibrary() {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/templates/library`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setOwnedTemplates(new Set(data.templates?.map(t => t.id) || []));
      }
    } catch (err) {
      // Ignore errors - user might not be logged in
    }
  }

  function applyFilters() {
    let filtered = [...templates];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t =>
        t.title?.toLowerCase().includes(query) ||
        t.summary?.toLowerCase().includes(query) ||
        t.tags?.some(tag => String(tag).toLowerCase().includes(query))
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter(t => t.category === categoryFilter);
    }

    // Price filter
    if (priceFilter === 'free') {
      filtered = filtered.filter(t => t.pricing_tier === 'free');
    } else if (priceFilter === 'premium') {
      filtered = filtered.filter(t => t.pricing_tier === 'premium');
    }

    setFilteredTemplates(filtered);
  }

  async function handleTemplateClick(template) {
    // Fetch full template details with HTML
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/templates/${encodeURIComponent(template.id)}`, {
        headers: {
          'Content-Type': 'application/json',
          ...(localStorage.getItem('accessToken')
            ? { Authorization: `Bearer ${localStorage.getItem('accessToken')}` }
            : {}),
        },
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSelectedTemplate(data.template);
      setShowDetailModal(true);
    } catch (err) {
      toast.error('Failed to load template details: ' + err.message);
    }
  }

  async function handleGetFreeTemplate() {
    if (!selectedTemplate) return;

    const token = localStorage.getItem('accessToken');
    if (!token) {
      toast.error('Please log in to get templates');
      return;
    }

    // For free templates, we just add them to the user's library
    // In the backend, this is automatic (free templates are always accessible)
    // We just need to refresh the library
    await loadUserLibrary();
    toast.success(`Added "${selectedTemplate.title}" to your library!`);
    setShowDetailModal(false);
  }

  async function handlePurchaseTemplate() {
    if (!selectedTemplate) return;

    const token = localStorage.getItem('accessToken');
    if (!token) {
      toast.error('Please log in to purchase templates');
      return;
    }

    setPurchasing(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1'}/templates/${encodeURIComponent(selectedTemplate.id)}/purchase`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || `HTTP ${res.status}`);
      }

      const data = await res.json();
      toast.success(data.message || 'Purchase successful!');

      // Refresh library and close modal
      await loadUserLibrary();
      setShowDetailModal(false);
    } catch (err) {
      toast.error(err.message || 'Purchase failed');
    } finally {
      setPurchasing(false);
    }
  }

  // Extract unique categories from templates
  const categories = ['all', ...new Set(templates.map(t => t.category).filter(Boolean))];

  return (
    <div className="Marketplace">
      <div className="MarketplaceHeader">
        <h1>Template Marketplace</h1>
        <p className="MarketplaceSubtitle">
          Discover professional templates to boost your productivity
        </p>
      </div>

      {/* Filters */}
      <div className="MarketplaceFilters">
        <input
          type="text"
          placeholder="Search templates..."
          className="MarketplaceSearch"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />

        <select
          className="MarketplaceFilterSelect"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          {categories.map(cat => (
            <option key={cat} value={cat}>
              {cat === 'all' ? 'All Categories' : cat}
            </option>
          ))}
        </select>

        <select
          className="MarketplaceFilterSelect"
          value={priceFilter}
          onChange={(e) => setPriceFilter(e.target.value)}
        >
          <option value="all">All Prices</option>
          <option value="free">Free</option>
          <option value="premium">Premium</option>
        </select>
      </div>

      {/* Template Grid */}
      {loading ? (
        <div className="MarketplaceLoading">Loading templates...</div>
      ) : filteredTemplates.length === 0 ? (
        <div className="MarketplaceEmpty">
          {searchQuery || categoryFilter !== 'all' || priceFilter !== 'all'
            ? 'No templates match your filters'
            : 'No templates available'}
        </div>
      ) : (
        <div className="MarketplaceGrid">
          {filteredTemplates.map((template) => {
            const isOwned = ownedTemplates.has(template.id);
            const isFree = template.pricing_tier === 'free';

            return (
              <div
                key={template.id}
                className="MarketplaceCard"
                onClick={() => handleTemplateClick(template)}
              >
                <div className="MarketplaceCardBadges">
                  {isFree ? (
                    <span className="MarketplaceBadge MarketplaceBadgeFree">FREE</span>
                  ) : isOwned ? (
                    <span className="MarketplaceBadge MarketplaceBadgeOwned">Purchased</span>
                  ) : (
                    <span className="MarketplaceBadge MarketplaceBadgePremium">
                      £{parseFloat(template.price_gbp || 0).toFixed(2)}
                    </span>
                  )}
                  {template.featured && (
                    <span className="MarketplaceBadge MarketplaceBadgeFeatured">⭐ Featured</span>
                  )}
                </div>

                <h3 className="MarketplaceCardTitle">{template.title}</h3>
                <p className="MarketplaceCardSummary">{template.summary}</p>

                {template.category && (
                  <div className="MarketplaceCardCategory">{template.category}</div>
                )}

                {template.tags && template.tags.length > 0 && (
                  <div className="MarketplaceCardTags">
                    {template.tags.slice(0, 3).map((tag, i) => (
                      <span key={i} className="MarketplaceCardTag">{String(tag)}</span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Template Detail Modal */}
      {showDetailModal && selectedTemplate && (
        <div
          className="ModalOverlay"
          onClick={(e) => e.target === e.currentTarget && setShowDetailModal(false)}
        >
          <div className="TaskEditPanel MarketplaceDetailModal" role="dialog" aria-modal="true">
            <div className="TaskEditHeader">
              <div className="TaskEditHeaderLeft">
                <div className="TaskEditHeading">{selectedTemplate.title}</div>
                <div className="TaskEditSub">{selectedTemplate.summary}</div>
              </div>
              <div className="TaskEditHeaderRight">
                <button className="Btn" onClick={() => setShowDetailModal(false)}>Close</button>
              </div>
            </div>

            {/* Preview */}
            <div className="MarketplaceDetailPreview">
              {selectedTemplate.html ? (
                <div
                  className="MarketplaceDetailPreviewContent"
                  dangerouslySetInnerHTML={{ __html: selectedTemplate.html }}
                />
              ) : selectedTemplate.fields?.interactive ? (
                <div className="MarketplaceDetailPreviewContent">
                  <h4 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600 }}>Interactive Template</h4>

                  {selectedTemplate.fields.variables && selectedTemplate.fields.variables.length > 0 && (
                    <div style={{ marginBottom: 16 }}>
                      <h5 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
                        You will be prompted for:
                      </h5>
                      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--text-primary)' }}>
                        {selectedTemplate.fields.variables.map((v, i) => (
                          <li key={i} style={{ marginBottom: 4 }}>
                            <strong>{v.label}</strong>
                            {v.required && <span style={{ color: '#dc2626' }}> *</span>}
                            {v.description && <span style={{ color: 'var(--text-secondary)' }}> - {v.description}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {selectedTemplate.fields.tasks && selectedTemplate.fields.tasks.length > 0 && (
                    <div>
                      <h5 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>
                        Will create {selectedTemplate.fields.tasks.length} task{selectedTemplate.fields.tasks.length !== 1 ? 's' : ''}:
                      </h5>
                      <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--text-primary)' }}>
                        {selectedTemplate.fields.tasks.slice(0, 5).map((t, i) => (
                          <li key={i} style={{ marginBottom: 4 }}>
                            {t.title}
                            {t.condition && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}> (conditional)</span>}
                          </li>
                        ))}
                        {selectedTemplate.fields.tasks.length > 5 && (
                          <li style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                            ... and {selectedTemplate.fields.tasks.length - 5} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="MarketplaceDetailPreviewEmpty">No preview available</div>
              )}
            </div>

            {/* Actions */}
            <div className="TaskEditActions" style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              {selectedTemplate.pricing_tier === 'free' ? (
                <button
                  className="Btn primary"
                  onClick={handleGetFreeTemplate}
                  style={{ background: '#10b981', borderColor: '#10b981' }}
                >
                  Get Template (Free)
                </button>
              ) : ownedTemplates.has(selectedTemplate.id) ? (
                <button className="Btn primary" disabled>
                  Already Purchased
                </button>
              ) : (
                <button
                  className="Btn primary"
                  onClick={handlePurchaseTemplate}
                  disabled={purchasing}
                  style={{ background: '#3b82f6', borderColor: '#3b82f6' }}
                >
                  {purchasing ? 'Processing...' : `Purchase for £${parseFloat(selectedTemplate.price_gbp || 0).toFixed(2)}`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
